import { getLinearColorStops, warm15DispersedAnchors } from '@methanesat/colors';
import { useTranslate } from '../../../hooks';
import { AreaEmissionsProducts, MethaneLayerIds, Platforms } from '../../../types';
import {
    ColorPoint,
    ColorScales,
    ColorStopLabel,
    EMISSIONS_RANGES,
    MSATL4RasterColorMaps,
    scaleData,
    targetScaleLabels,
    warmLinearDispersedAnchors
} from '../../../consts';
import { getColorRangeColors } from '../../../utils/colors';
import { Theme } from '@methanesat/ui-components';

/**
 * Creates labels for color stops from a min/max, segmenting linearly.
 */
export const getColorStopLabels = (
    min: number,
    max: number,
    colorStopCount = 4,
    ascending = false
): ColorStopLabel[] => {
    const stops = getLinearColorStops(colorStopCount, min, max);

    return stops.map((stopVal, index) => {
        const percentageStep = parseFloat(((index / (colorStopCount - 1)) * 100).toFixed(2));
        const label = Math.floor(stopVal).toLocaleString();

        return {
            percent: ascending ? 100 - percentageStep : percentageStep,
            label,
            key: label
        };
    });
};

/**
 * Adds to target labels "or more/less" text (using locales), tooltips, etc.  Intended to be used after running
 * getColorStopLabels().
 */
export const useAugmentLabels = (labels: ColorStopLabel[], unitsKey: string, tooltip: string) => {
    const t = useTranslate();
    return labels.map((labelObj) => {
        const { percent, label, key } = labelObj;

        // Highest value.
        if (percent === 100) {
            return {
                key,
                label: t('emissionsMapPage.miniLegend.generalEmissions.orMore', {
                    value: label,
                    unit: t(`emissionsMapPage.units.${unitsKey}`)
                }),
                percent,
                tooltip: t(tooltip)
            };
        }

        // Lowest value.
        if (percent === 0) {
            return {
                key,
                label: t('emissionsMapPage.miniLegend.generalEmissions.orLess', {
                    value: label
                }),
                percent
            };
        }

        // No-op passthrough.
        return labelObj;
    });
};

/** Generates color stop labels specific to a platform and type (target or dispersed sources). */
export const generateColorStopLabels = (platform: Platforms, layerId: MethaneLayerIds) => {
    if (layerId === MethaneLayerIds.targets) {
        return targetScaleLabels[platform];
    }

    if (layerId === MethaneLayerIds.areaEmissionRaster && platform === Platforms.MAIR) {
        const { MAX } = EMISSIONS_RANGES[platform][MethaneLayerIds.areaEmissionRaster];
        return getColorStopLabels(1, MAX);
    }

    return [];
};

/** Returns an array of {percent, color} objects to be passed to the mini legend thermometer for L3 and L4 data.
 *
 * **L3 data**: Maps colors based on logarithmic stops, globally.
 * **MSAT L4 data**: Maps colors using predefined anchor positions.
 * **MAIR L4 data**: Evenly distributes colors across the palette.
 */
export const getEmissionsThermometerColors = (
    platform: Platforms,
    product: AreaEmissionsProducts,
    colors: string[]
) => {
    if (platform === Platforms.MSAT && product === AreaEmissionsProducts.l4) {
        return colors.map((color, index) => ({
            percent: warm15DispersedAnchors[index],
            color: color
        }));
    }
    if (platform === Platforms.MAIR && product === AreaEmissionsProducts.l4) {
        return colors.map((color, index) => ({
            percent: warmLinearDispersedAnchors[index],
            color: color
        }));
    } else {
        return colors.map((color, index, arr) => {
            return {
                percent: (index / arr.length) * 100,
                color
            };
        });
    }
};

/** Returns an array of {percent, color} objects to be passed to the mini legend color switcher thermometer for L4 MSAT data.
 * Maps colors using predefined anchor positions.
 * */
export const getL4MSATColorArrays = (scale: MSATL4RasterColorMaps, colors: string[]): ColorPoint[] => {
    const { anchors } = scaleData[scale];

    return colors.map((color, index) => ({
        percent: anchors[index],
        color
    }));
};

/**
 * Returns colors mapped to positions based on predefined anchors and combines them with labels for each MSAT L4 color range.
 * @param theme - used to determine the opacity of the colors when layer is disabled
 * @param areaEmissionLayerEnabled
 * @returns ColorScales object with MSATL4RasterColorMaps scale values as keys
 */
export const getL4MSATLabels = (theme: Theme, areaEmissionLayerEnabled: boolean): ColorScales => {
    const scales = Object.keys(scaleData) as MSATL4RasterColorMaps[];

    const results = scales
        .filter((scaleName) => scaleName.includes('msat'))
        .map((scale) => {
            const { labels } = scaleData[scale];

            return [
                scale,
                {
                    colors: getL4MSATColorArrays(
                        scale,
                        getColorRangeColors(
                            theme,
                            areaEmissionLayerEnabled,
                            AreaEmissionsProducts.l4,
                            Platforms.MSAT,
                            scale
                        )
                    ),
                    labels
                }
            ];
        });

    return Object.fromEntries(results) as ColorScales;
};

export const getBarGradientWidth = (scale: string) => {
    return scaleData[scale as MSATL4RasterColorMaps]?.gradientWidth;
};
